// Чистата логика, обща за таблиците на справките: търсене по думи, сортиране,
// ↑/↓ по редовете и класовете на ниските редове. Филтрите в колоните (и
// фасетните им броячи) са в `components/filters/columnFilterModel.ts`.
import type { KeyboardEvent } from "react";
import { ROW_HEADER, ROW_HEADER_TEXT, ROW_READ, ROW_TOTAL } from "../../components/list/density";

// Inventory.tsx още спуска общите props на справките (onOpenMobileMenu,
// isNarrow, warehouseId…); страниците ги четат от useInventoryShell()/useViewport
// и типът е само за да приемат спуснатото.
export type LegacyReportProps = { isNarrow?: boolean; warehouseId?: number | null };

export type SortDir = "asc" | "desc";
export type ReportSort<K extends string> = { key: K; dir: SortDir };

// Заглавията на колоните в таблиците на справките (едни и същи навсякъде)
export const REPORT_HEAD_CLASS = ROW_HEADER_TEXT;
// Етикетът на реда „Общо за периода" — вдясно, точно пред сумите (иначе
// отляво изглежда като броя на документите)
export const REPORT_TOTAL_LABEL_CLASS = `${ROW_HEADER_TEXT} justify-self-end text-right`;

// Ниските редове на таблиците (справката е за четене, не за пръст): ред ~34px
// с 13px текст и ЕДИН ред съдържание — второстепенното стои приглушено в
// същия ред; заглавието ~30px, редът „Общо" ~36px. Картите на телефон са си
// високи (там редът е за пръст). Височините са от общия слой `density.ts`.
export const REPORT_HEADER_ROW_CLASS = `border-b border-shade-2 ${ROW_HEADER}`;
export const REPORT_ROW_CLASS = `border-t border-shade-1 ${ROW_READ}`;
export const REPORT_OPENABLE_ROW_CLASS = `cursor-pointer border-t border-shade-1 ${ROW_READ} outline-none hover:bg-fill focus-visible:bg-fill`;
export const REPORT_TOTAL_ROW_CLASS = `border-t border-shade-2 bg-fill ${ROW_TOTAL}`;

// Думите от търсачката: всяка трябва да се среща някъде в реда
export const searchWords = (query: string) =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

export const matchesWords = (
  parts: (string | null | undefined)[],
  words: string[],
) => {
  if (!words.length) return true;
  const haystack = parts.filter(Boolean).join(" ").toLowerCase();
  return words.every((word) => haystack.includes(word));
};

// Клик на заглавие: същата колона обръща посоката, нова започва от `firstDir`
export const nextSort = <K extends string>(
  current: ReportSort<K> | null,
  key: K,
  firstDir: SortDir,
): ReportSort<K> =>
  current?.key === key
    ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
    : { key, dir: firstDir };

// Без сортиране → подредбата от бекенда/модела остава
export const sortRows = <T, K extends string>(
  rows: T[],
  sort: ReportSort<K> | null,
  compare: (a: T, b: T, key: K) => number,
): T[] => {
  if (!sort) return rows;
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => compare(a, b, sort.key) * sign);
};

export const compareText = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? "").localeCompare(b ?? "", "bg", { numeric: true });

export const compareNumber = (a: number | null | undefined, b: number | null | undefined) =>
  (a ?? 0) - (b ?? 0);

// ↑/↓ местят фокуса по редовете (`data-row`) в контейнера; Enter ги отваря
// (виж onKeyDown на самия ред)
export const moveRowFocus = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
  const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("[data-row]"));
  const index = rows.indexOf(document.activeElement as HTMLElement);
  if (index < 0) return;
  const next = event.key === "ArrowDown" ? index + 1 : index - 1;
  if (next < 0 || next >= rows.length) return;
  event.preventDefault();
  rows[next].focus();
};

// Атрибутите на ред, който се отваря с клик или Enter
export const openableRowProps = (onOpen: () => void) => ({
  "data-row": true,
  tabIndex: 0,
  role: "button" as const,
  onClick: onOpen,
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" && event.target === event.currentTarget) {
      event.preventDefault();
      onOpen();
    }
  },
});
